/**
 * The route ascent document `gyms/{gymId}/ascents/{uid}_{routeId}` — one per
 * climber and route, written on the first send. The shape every writer builds
 * through this one function: the client's send (`useMainApp.logClimb`), the
 * competition close sync (`apps/functions/shared/competitionClimbs.js`) and the
 * E2E seed (tests/e2e/seed/). docs/spec/04-data-model-gym.md.
 *
 * The route's display fields are denormalized onto the ascent itself, so every
 * consumer reads them directly instead of joining against `routes` — critical
 * once the route is archived and drops out of the active-only listener, which
 * is what keeps historical stats intact. `repeatCount` starts at 0; the repeat
 * half is server-maintained (`updateClimbLogStats`).
 */

/** The ascent's document id: one document per climber and route. Re-running a sync never makes a second copy. */
export function ascentDocId(uid, routeId) {
  return `${uid}_${routeId}`;
}

/**
 * @param {object} p
 * @param {string} p.uid                 the climber
 * @param {string} p.routeId
 * @param {object|null} [p.route]        the route's display fields (`name`, `difficulty`, `holdColor`, `discipline`), or null when unknown
 * @param {string} p.date                the gym's day of the send, "YYYY-MM-DD"
 * @param {boolean} [p.flash]            a first-try send; the key is written only when true
 * @param {number|null} [p.attempts]     the tries the send took, when known
 * @param {string|null} [p.gradeVote]    the climber's grade opinion — the caller validates it
 * @param {string|null} [p.compId]       the competition a closed competition's top came from
 * @param {*} [p.createdAt]              the climb's timestamp; left out when undefined (an SDK
 *                                       sentinel or Timestamp is the caller's: this module is SDK-free)
 * @param {*} [p.lastClimbedAt]          likewise
 */
export function buildAscentDoc({
  uid, routeId, route = null, date,
  flash = false, attempts = null, gradeVote = null, compId = null,
  createdAt, lastClimbedAt,
}) {
  const doc = {
    userId: uid,
    routeId,
    date,
    routeName: route?.name || "",
    routeDifficulty: route?.difficulty ?? null,
    routeHoldColor: route?.holdColor ?? null,
    routeDiscipline: route?.discipline || "boulder",
    repeatCount: 0,
  };
  if (createdAt !== undefined) doc.createdAt = createdAt;
  if (lastClimbedAt !== undefined) doc.lastClimbedAt = lastClimbedAt;
  if (compId != null) doc.compId = compId;
  if (flash) doc.flash = true;
  if (attempts != null) doc.attempts = attempts;
  if (gradeVote != null) doc.gradeVote = gradeVote;
  return doc;
}
